import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtUGX, fmtNum } from "@/lib/format";
import { LiveBusMap } from "@/components/live-bus-map";
import {
  Bus, Users, AlertTriangle, MapPin, ShieldCheck, Gauge, Wrench, ScanLine, Camera,
  Clock, Sparkles, Brain, Cloud, Route as RouteIcon, Crosshair, Activity, CheckCircle2,
  PhoneCall, Award, ChevronRight, Search, Home, ArrowRight, GraduationCap, Building2,
  Briefcase, TrendingUp, Leaf, Trophy, Wallet, BadgeCheck, Radar, Star, Target, Zap,
  CloudRain, WifiOff, BookOpen, HandCoins, Plus, Filter, Download, Send, X, Eye, ThumbsUp,
  Siren, Megaphone, FileText, Hammer, TrendingDown, ShieldAlert, Timer, DollarSign,
} from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar as RRadar, AreaChart, Area } from "recharts";

const fmtCompact = (n: number) => n >= 1e9 ? `UGX ${(n/1e9).toFixed(1)}B` : n >= 1e6 ? `UGX ${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `UGX ${(n/1e3).toFixed(0)}K` : `UGX ${n}`;


export function TransportCommandCenter() {
  const [tab, setTab] = useState("ops");
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [postRfqOpen, setPostRfqOpen] = useState(false);
  const [bidsRfq, setBidsRfq] = useState<any | null>(null);
  const [copilotQ, setCopilotQ] = useState("");
  const [copilotLog, setCopilotLog] = useState<{ q: string; a: string }[]>([]);
  const [marketSearch, setMarketSearch] = useState("");
  const [marketDistrict, setMarketDistrict] = useState<string>("all");
  const [routeAction, setRouteAction] = useState<{ route: any; mode: "map" | "manifest" | "fare" | "optimize" } | null>(null);
  const [poolAction, setPoolAction] = useState<"merge" | "balance" | "optimize" | null>(null);
  const [appliedPool, setAppliedPool] = useState<Record<string, boolean>>({});

  const act = (msg: string) => toast.success(msg);

  const { data: positions = [] } = useQuery({
    queryKey: ["tcc-positions"], refetchInterval: 8000,
    queryFn: async () => (await supabase.from("bus_positions" as any).select("*")).data ?? [],
  });
  const { data: schools = [] } = useQuery({
    queryKey: ["tcc-schools"],
    queryFn: async () => (await supabase.from("schools").select("id,name,district,student_count").limit(50)).data ?? [],
  });


  const kpis = useMemo(() => ({
    fleet: 248, active: 219, maintenance: 18, offline: 11, gpsOffline: 7,
    schoolsTotal: 96, schoolsActive: 88, studentsAssigned: 18_420,
    tripsScheduled: 612, tripsActive: 184, tripsCompleted: 397, tripsDelayed: 9,
    onboard: 8_142, unverified: 23, deviations: 2, emergencies: 0,
    operators: 14, routes: 142, stops: 1_286, multiSchoolRoutes: 47,
    safeJourneyPct: 99.4, avgUtilization: 78,
  }), []);

  // National operator demo network
  const operators = useMemo(() => [
    { id: "op-1", name: "Kampala Mobility Co.", fleet: 64, schools: 22, safety: 96, onTime: 97.2, revenue: 184_500_000, rating: 4.8, verified: true },
    { id: "op-2", name: "Pearl Schools Transit", fleet: 48, schools: 18, safety: 94, onTime: 95.1, revenue: 142_800_000, rating: 4.7, verified: true },
    { id: "op-3", name: "Wakiso Safe Rides", fleet: 36, schools: 14, safety: 92, onTime: 93.8, revenue: 98_200_000, rating: 4.6, verified: true },
    { id: "op-4", name: "Mukono Yellow Lines", fleet: 28, schools: 9, safety: 89, onTime: 91.4, revenue: 67_400_000, rating: 4.4, verified: true },
    { id: "op-5", name: "Entebbe Express Kids", fleet: 22, schools: 7, safety: 95, onTime: 96.3, revenue: 58_100_000, rating: 4.7, verified: true },
    { id: "op-6", name: "Jinja Education Transit", fleet: 18, schools: 6, safety: 87, onTime: 89.2, revenue: 41_300_000, rating: 4.2, verified: false },
    { id: "op-7", name: "Mbarara Pupil Movers", fleet: 16, schools: 5, safety: 91, onTime: 92.8, revenue: 36_700_000, rating: 4.5, verified: true },
  ], []);

  // Multi-school shared routes
  const sharedRoutes = useMemo(() => [
    { code: "KLA-NORTH-01", name: "Naalya → Ntinda → CBD", capacity: 65, onboard: 52, schools: ["Greenhill Academy","St Mary's College Kisubi","Kampala Parents School"], stops: 12, profit: 3_500_000, color: "#B33A1A" },
    { code: "KLA-WEST-04",  name: "Nansana → Kasubi → Mengo", capacity: 60, onboard: 47, schools: ["Mengo SS","Kampala Quality","Buganda Road PS"], stops: 14, profit: 2_900_000, color: "#16a34a" },
    { code: "WAK-02",       name: "Wakiso → Kawempe → CBD", capacity: 70, onboard: 61, schools: ["Wakiso Hill","Old Kampala","Mariam High"], stops: 16, profit: 4_100_000, color: "#0ea5e9" },
    { code: "ENT-01",       name: "Entebbe → Kajjansi → Kibuye", capacity: 55, onboard: 38, schools: ["Entebbe SS","Lake Vic Primary"], stops: 9, profit: 2_200_000, color: "#a855f7" },
    { code: "MUK-03",       name: "Mukono → Seeta → Bweyogerere", capacity: 60, onboard: 44, schools: ["Seeta High","Bishop Senior","Namilyango"], stops: 11, profit: 2_700_000, color: "#f59e0b" },
    { code: "JIN-01",       name: "Njeru → Bugembe → Jinja CBD", capacity: 55, onboard: 39, schools: ["Jinja SS","Busoga College","Wanyange Girls"], stops: 10, profit: 2_050_000, color: "#ec4899" },
  ], []);

  const utilization7d = useMemo(() => Array.from({length:7}).map((_,i)=>({day:["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][i], util: [76,79,81,78,82,45,12][i]})), []);
  const demandByDistrict = useMemo(() => [
    { d:"Kampala",  demand: 6_200, supply: 5_800 },
    { d:"Wakiso",   demand: 4_400, supply: 3_900 },
    { d:"Mukono",   demand: 2_100, supply: 1_800 },
    { d:"Entebbe",  demand: 1_600, supply: 1_500 },
    { d:"Jinja",    demand: 1_200, supply:   900 },
    { d:"Mbarara",  demand:   900, supply:   700 },
  ], []);

  const driverScorecard = useMemo(() => [
    { k:"On-time", v: 96 }, { k:"Speed compl.", v: 92 }, { k:"Safety", v: 95 },
    { k:"Parent rating", v: 94 }, { k:"Verification", v: 98 }, { k:"Eco-driving", v: 88 },
  ], []);

  const safetyEvents = [
    { id:1, kind:"Missed pickup", route:"KLA-NORTH-01", time:"06:54", student:"Maya N.", severity:"warn" },
    { id:2, kind:"Wrong bus boarding (auto-corrected)", route:"WAK-02", time:"07:02", student:"Brian S.", severity:"warn" },
    { id:3, kind:"Route deviation > 400m", route:"KLA-WEST-04", time:"16:38", student:"—", severity:"warn" },
    { id:4, kind:"Speed 62 in 50 zone", route:"MUK-03", time:"06:51", student:"—", severity:"info" },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-2xl border bg-gradient-to-br from-primary/10 via-card to-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary text-primary-foreground"><Radar className="h-5 w-5" /></div>
              <div>
                <div className="font-display text-2xl">TriSphere Mobility Network</div>
                <div className="text-sm text-muted-foreground">Every child's journey. Verified. Protected. Connected · {fmtNum(kpis.schoolsActive)} schools · {fmtNum(kpis.operators)} operators · {fmtNum(kpis.fleet)} vehicles</div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setTab("safety")} className="inline-flex items-center gap-1 rounded-full border border-success/30 bg-success/15 px-2.5 py-0.5 text-xs text-success hover:bg-success/25 transition">
              <ShieldCheck className="h-3 w-3" />Safe journeys {kpis.safeJourneyPct}%
            </button>
            <button onClick={() => setTab("ops")} className="inline-flex items-center gap-1 rounded-full border bg-background px-2.5 py-0.5 text-xs hover:bg-muted transition">
              <Activity className="h-3 w-3" />{kpis.tripsActive} trips active
            </button>
            <Button size="sm" variant="outline" onClick={() => setDispatchOpen(true)}><PhoneCall className="mr-2 h-3.5 w-3.5" />Dispatch ops</Button>
          </div>

        </div>
      </div>

      {/* Executive KPIs */}
      <div className="grid gap-3 md:grid-cols-4 lg:grid-cols-6">
        <Kpi i={Bus} label="Fleet active" value={`${kpis.active}/${kpis.fleet}`} sub={`${kpis.maintenance} in shop · ${kpis.offline} offline`} tone="primary" />
        <Kpi i={Building2} label="Schools served today" value={`${kpis.schoolsActive}/${kpis.schoolsTotal}`} sub={`${fmtNum(kpis.studentsAssigned)} students assigned`} tone="info" />
        <Kpi i={RouteIcon} label="Trips active" value={fmtNum(kpis.tripsActive)} sub={`${kpis.tripsDelayed} delayed · ${kpis.tripsCompleted} done`} tone="success" />
        <Kpi i={Users} label="Onboard now" value={fmtNum(kpis.onboard)} sub={`${kpis.unverified} unverified`} tone="info" />
        <Kpi i={ShieldCheck} label="Safe journeys" value={`${kpis.safeJourneyPct}%`} sub={`${kpis.deviations} deviation · ${kpis.emergencies} emergencies`} tone="success" />
        <Kpi i={Briefcase} label="Multi-school routes" value={fmtNum(kpis.multiSchoolRoutes)} sub={`${kpis.avgUtilization}% avg utilization`} tone="warn" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="ops">National map</TabsTrigger>
          <TabsTrigger value="passport">Journey passport</TabsTrigger>
          <TabsTrigger value="routes">Shared routes</TabsTrigger>
          <TabsTrigger value="operators">Operators network</TabsTrigger>
          <TabsTrigger value="marketplace">Marketplace</TabsTrigger>
          <TabsTrigger value="schools">Schools served</TabsTrigger>
          <TabsTrigger value="safety">Safety center</TabsTrigger>
          <TabsTrigger value="drivers">Drivers</TabsTrigger>
          <TabsTrigger value="demand">Demand & heatmap</TabsTrigger>
          <TabsTrigger value="finance">Revenue & billing</TabsTrigger>
          <TabsTrigger value="weather">Weather & roads</TabsTrigger>
          <TabsTrigger value="green">Green score</TabsTrigger>
          <TabsTrigger value="copilot">AI copilot</TabsTrigger>
        </TabsList>

        {/* NATIONAL MAP */}
        <TabsContent value="ops" className="mt-4 space-y-4">
          <LiveBusMap height={600} autoTick canViewTrails canViewHistory title="Uganda Mobility Grid · live telematics" />
          <div className="grid gap-3 md:grid-cols-3">
            <Card className="p-4 shadow-card">
              <div className="font-display text-base">Map legend</div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <Legend2 color="#0ea5e9" label="🏫 Schools" />
                <Legend2 color="#f59e0b" label="🚌 Buses" />
                <Legend2 color="#16a34a" label="🚏 Stops" />
                <Legend2 color="#B33A1A" label="⚠ Safety events" />
                <Legend2 color="#a855f7" label="📈 Demand zone" />
                <Legend2 color="#64748b" label="⚫ GPS offline" />
              </div>
            </Card>
            <Card className="p-4 shadow-card">
              <div className="font-display text-base">Real-time signals</div>
              <div className="mt-3 space-y-2 text-sm">
                <Signal label="Average delay" value="3.4 min" tone="ok" />
                <Signal label="On-time arrival" value="96.2%" tone="ok" />
                <Signal label="Verified boardings · today" value={fmtNum(8_119)} tone="ok" />
                <Signal label="Route deviations" value={`${kpis.deviations} active`} tone="warn" />
                <Signal label="Emergencies" value="0 active" tone="ok" />
              </div>
            </Card>
            <Card className="p-4 shadow-card">
              <div className="font-display text-base">Right-now ops</div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <Mini label="Onboard" value={fmtNum(kpis.onboard)} />
                <Mini label="At stops" value="412" />
                <Mini label="At schools" value="9,847" />
                <Mini label="Home zone" value="—" />
              </div>
              <Button size="sm" variant="outline" className="mt-3 w-full" onClick={() => { setTab("safety"); act("Focused on active incidents"); }}><Crosshair className="mr-2 h-3.5 w-3.5" />Focus on incidents</Button>
            </Card>
          </div>
        </TabsContent>

        {/* JOURNEY PASSPORT */}
        <TabsContent value="passport" className="mt-4 space-y-4">
          <Card className="p-5 shadow-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-to-br from-primary to-primary-glow text-primary-foreground font-display text-lg">SN</div>
                <div>
                  <div className="flex items-center gap-2 font-display text-lg">Sarah Namukasa <BadgeCheck className="h-4 w-4 text-primary" /></div>
                  <div className="text-xs text-muted-foreground">S2 · St Mary's SS Kitende · Route KLA-NORTH-01 · seat 12B</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="gap-1 bg-success/10 text-success border-success/30"><ShieldCheck className="h-3 w-3" />Safety score 100</Badge>
                <Badge variant="outline">This term · 47 safe journeys</Badge>
                <Button size="sm" variant="outline" onClick={() => act("Passport shared with parent via SMS + app")}>Share with parent</Button>
              </div>
            </div>
            <div className="mt-5 grid gap-6 md:grid-cols-2">
              <JourneyTimeline />
              <div className="space-y-3">
                <div className="rounded-xl border bg-card p-4">
                  <div className="font-display text-sm">Term passport · verified events</div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                    <Mini label="Safe journeys" value="47/47" />
                    <Mini label="Attendance" value="96%" />
                    <Mini label="Travel reliability" value="98%" />
                    <Mini label="Avg travel time" value="34 min" />
                    <Mini label="Lunch taken" value="42/47" />
                    <Mini label="Home arrivals" value="47/47" />
                  </div>
                </div>
                <div className="rounded-xl border bg-card p-4">
                  <div className="font-display text-sm">Welfare indicators</div>
                  <div className="mt-3 space-y-2 text-sm">
                    <Signal label="Travel time vs class average" value="-6 min" tone="ok" />
                    <Signal label="Pickup reliability" value="100%" tone="ok" />
                    <Signal label="Driver rating" value="4.9 ★" tone="ok" />
                    <Signal label="Bus cabin score" value="A" tone="ok" />
                  </div>
                </div>
                <div className="rounded-xl border bg-primary/5 p-4">
                  <div className="flex items-center gap-2 font-display text-sm"><Sparkles className="h-4 w-4 text-primary" />AI welfare insight</div>
                  <p className="mt-2 text-sm text-muted-foreground">Sarah's travel time is 6 minutes below class average — strong academic-mobility correlation. Pickup window of 06:54 is optimal; suggest keeping current route assignment for next term.</p>
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* SHARED ROUTES */}
        <TabsContent value="routes" className="mt-4 space-y-4">
          {/* Network impact KPIs */}
          <div className="grid gap-3 md:grid-cols-5">
            {[
              { label: "Pooled routes", v: `${kpis.multiSchoolRoutes}`, sub: "across 38 schools", icon: RouteIcon, tone: "from-primary/10 to-card" },
              { label: "Avg seats filled", v: `${kpis.avgUtilization}%`, sub: "+24pp vs solo routes", icon: Gauge, tone: "from-emerald-500/10 to-card" },
              { label: "Parent savings", v: "UGX 41M", sub: "per term · pooled fares", icon: HandCoins, tone: "from-amber-500/10 to-card" },
              { label: "CO₂ avoided", v: "112 t", sub: "vs single-school fleets", icon: Leaf, tone: "from-green-500/10 to-card" },
              { label: "Empty-km cut", v: "−38%", sub: "AI deadhead optimizer", icon: Sparkles, tone: "from-violet-500/10 to-card" },
            ].map((k, i) => (
              <div key={i} className={`rounded-xl border bg-gradient-to-br ${k.tone} p-3`}>
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><k.icon className="h-3.5 w-3.5" />{k.label}</div>
                <div className="mt-1 font-display text-xl">{k.v}</div>
                <div className="text-[10px] text-muted-foreground">{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Route cards with deeper detail */}
          <Card className="p-4 shadow-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-display text-base">Multi-school shared routes</div>
                <div className="text-xs text-muted-foreground">One bus → many stops → multiple schools · live pooling, fare-split, carbon & SLA</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setPoolAction("merge")}><Crosshair className="mr-2 h-3.5 w-3.5" />Simulate merge</Button>
                <Button size="sm" variant="outline" onClick={() => setPoolAction("balance")}><Brain className="mr-2 h-3.5 w-3.5" />Re-balance schools</Button>
                <Button size="sm" onClick={() => setPoolAction("optimize")}><Zap className="mr-2 h-3.5 w-3.5" />Run AI optimizer</Button>
              </div>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {sharedRoutes.map((r) => {
                const util = Math.round((r.onboard/r.capacity)*100);
                const fareSplit = r.schools.map((_,i)=> Math.round(100/r.schools.length) + (i===0 ? (100 - Math.round(100/r.schools.length)*r.schools.length) : 0));
                const co2Saved = Math.round(r.onboard * 1.8);
                return (
                  <div key={r.code} className="rounded-xl border bg-card p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="h-3 w-3 rounded-full" style={{ background: r.color }} />
                        <span className="font-mono text-xs">{r.code}</span>
                        <span className="font-medium">{r.name}</span>
                      </div>
                      <Badge variant={util >= 85 ? "default" : "outline"}>{util}% full · {r.onboard}/{r.capacity}</Badge>
                    </div>

                    {/* utilization bar with school segments */}
                    <div className="mt-2 flex h-2 w-full overflow-hidden rounded-full bg-muted">
                      {r.schools.map((s, i) => (
                        <span key={i} title={s} className="h-full" style={{ width: `${util * (fareSplit[i]/100)}%`, background: `hsl(${(i*97)%360} 70% 50%)` }} />
                      ))}
                    </div>

                    {/* school chips with fare share */}
                    <div className="mt-3 space-y-1">
                      {r.schools.map((s,i)=>(
                        <div key={i} className="flex items-center justify-between text-[11px]">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="h-2 w-2 rounded-sm" style={{ background: `hsl(${(i*97)%360} 70% 50%)` }} />
                            <Building2 className="h-3 w-3 text-muted-foreground" />{s}
                          </span>
                          <span className="font-mono text-muted-foreground">{fareSplit[i]}% · {fmtCompact(Math.round(r.profit*fareSplit[i]/100))}</span>
                        </div>
                      ))}
                    </div>

                    {/* metrics row */}
                    <div className="mt-3 grid grid-cols-4 gap-2 border-t pt-3 text-center text-[10px]">
                      <div><div className="font-mono text-xs text-foreground">{r.stops}</div><div className="text-muted-foreground">stops</div></div>
                      <div><div className="font-mono text-xs text-foreground">42m</div><div className="text-muted-foreground">avg trip</div></div>
                      <div><div className="font-mono text-xs text-emerald-600">{co2Saved}kg</div><div className="text-muted-foreground">CO₂ saved</div></div>
                      <div><div className="font-mono text-xs text-success">+{fmtCompact(r.profit)}</div><div className="text-muted-foreground">/month</div></div>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-1.5">
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setRouteAction({ route: r, mode: "map" })}><MapPin className="mr-1 h-3 w-3" />View on map</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setRouteAction({ route: r, mode: "manifest" })}><Users className="mr-1 h-3 w-3" />Manifest</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setRouteAction({ route: r, mode: "fare" })}><Briefcase className="mr-1 h-3 w-3" />Fare split</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setRouteAction({ route: r, mode: "optimize" })}><Zap className="mr-1 h-3 w-3" />Re-optimize</Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Pooling matrix & AI insights */}
          <div className="grid gap-3 lg:grid-cols-3">
            <Card className="p-4 shadow-card lg:col-span-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display text-base">School ↔ School pooling matrix</div>
                  <div className="text-xs text-muted-foreground">Co-rider density. Darker = stronger candidate pair for shared routes.</div>
                </div>
                <Badge variant="outline" className="text-[10px]">Live · updated 5s</Badge>
              </div>
              <div className="mt-3 overflow-x-auto">
                <table className="text-[11px]">
                  <thead><tr><th className="p-1"></th>{["Greenhill","St Mary's","Mengo","Kampala P.","Wakiso H.","Seeta"].map(s=>(<th key={s} className="px-2 py-1 text-left font-medium text-muted-foreground">{s.slice(0,8)}</th>))}</tr></thead>
                  <tbody>
                    {["Greenhill","St Mary's","Mengo","Kampala P.","Wakiso H.","Seeta"].map((row, i) => (
                      <tr key={row}>
                        <td className="pr-2 text-right text-muted-foreground">{row.slice(0,8)}</td>
                        {Array.from({length:6}).map((_,j) => {
                          const v = i===j ? 0 : ((i*7 + j*11) % 100);
                          return <td key={j} className="p-0.5"><div className="grid h-8 w-12 place-items-center rounded font-mono text-[10px]" style={{ background: i===j ? "hsl(var(--muted))" : `hsla(160, 70%, 40%, ${v/120 + 0.08})`, color: v > 50 ? "white" : undefined }}>{i===j? "—" : v}</div></td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 grid gap-2 md:grid-cols-3 text-xs">
                <div className="rounded-lg border bg-muted/30 p-2"><div className="text-muted-foreground">Top suggested pairing</div><div className="font-medium">Wakiso Hill ↔ Mariam High</div><div className="text-[10px] text-emerald-600">+UGX 1.8M/mo · −1 bus</div></div>
                <div className="rounded-lg border bg-muted/30 p-2"><div className="text-muted-foreground">Underused capacity</div><div className="font-medium">ENT-01 · 31% empty seats</div><div className="text-[10px] text-amber-600">merge with KLA-WEST-04</div></div>
                <div className="rounded-lg border bg-muted/30 p-2"><div className="text-muted-foreground">Over-capacity risk</div><div className="font-medium">WAK-02 · peaks 105%</div><div className="text-[10px] text-rose-600">add midi-bus 03:15</div></div>
              </div>
            </Card>

            <Card className="p-4 shadow-card">
              <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /><div className="font-display text-base">AI optimizer insights</div></div>
              <ul className="mt-3 space-y-2 text-xs">
                <li className="rounded-lg border-l-2 border-emerald-500 bg-emerald-50/40 p-2"><div className="font-medium">Merge KLA-NORTH-01 + KLA-WEST-04</div><div className="text-muted-foreground">Shared 1.2km corridor on Bombo Rd · saves UGX 2.3M/mo, frees 1 bus, adds 12min to longest trip.</div></li>
                <li className="rounded-lg border-l-2 border-sky-500 bg-sky-50/40 p-2"><div className="font-medium">Stagger pickup 06:25→06:35</div><div className="text-muted-foreground">Reduces Naalya bottleneck. Greenhill arrives same time, lifts on-time by 4%.</div></li>
                <li className="rounded-lg border-l-2 border-amber-500 bg-amber-50/40 p-2"><div className="font-medium">Add midi-bus on WAK-02 16:00</div><div className="text-muted-foreground">Forecast 68 students vs 65 capacity Fri evening. Costs UGX 320k/wk, prevents standing.</div></li>
                <li className="rounded-lg border-l-2 border-violet-500 bg-violet-50/40 p-2"><div className="font-medium">Dynamic stop on-demand</div><div className="text-muted-foreground">3 parents in Bunga can join MUK-03 if it detours +1.4km on Tuesdays. +UGX 540k/mo.</div></li>
              </ul>
              <Button size="sm" className="mt-3 w-full" onClick={() => act("Applied 4 optimizations · parents notified")}><Brain className="mr-2 h-3.5 w-3.5" />Apply all (4 changes)</Button>
            </Card>
          </div>

          {/* Network value & what-else-can-we-do */}
          <Card className="p-4 shadow-card">
            <div className="flex items-center justify-between">
              <div className="font-display text-base">What pooling unlocks for the network</div>
              <Badge variant="outline" className="text-[10px]">Strategy</Badge>
            </div>
            <div className="mt-3 grid gap-3 md:grid-cols-4">
              {[
                { icon: HandCoins, t: "Fare-split billing", d: "Each school is auto-invoiced per child-km it consumed. Parents pay one fare; back-office reconciles." },
                { icon: Leaf, t: "Carbon credits", d: "Avoided emissions feed an annual ESG report. Eligible for tradable credits under VCS standards." },
                { icon: Radar, t: "Demand sensing", d: "Aggregated boarding heatmaps reveal new corridors before schools request them — open new routes proactively." },
                { icon: ShieldCheck, t: "Resilience pools", d: "Bus breakdown? Automatically reassigns nearby pooled bus with spare seats — no child stranded." },
                { icon: CloudRain, t: "Weather re-routing", d: "Heavy rain over Northern bypass triggers safer pooled detour; all 3 schools notified together." },
                { icon: Wallet, t: "Sponsored seats", d: "Corporates / alumni can sponsor empty seats on pooled routes for scholarship students." },
                { icon: BookOpen, t: "Field-trip pooling", d: "Off-peak buses sold by the hour to multiple schools for joint excursions — new revenue line." },
                { icon: Trophy, t: "Operator scorecards", d: "Pooled SLA (on-time, safety, NPS) ranks operators across schools — drives quality, not just price." },
              ].map((x, i) => (
                <div key={i} className="rounded-xl border bg-muted/30 p-3">
                  <div className="flex items-center gap-2"><x.icon className="h-4 w-4 text-primary" /><div className="font-medium text-sm">{x.t}</div></div>
                  <div className="mt-1 text-[11px] text-muted-foreground">{x.d}</div>
                </div>
              ))}
            </div>
          </Card>

          {/* Dynamic routing model */}
          <Card className="p-4 shadow-card">
            <div className="font-display text-base">Dynamic multi-school routing model</div>
            <div className="mt-3 grid items-center gap-3 md:grid-cols-7 text-xs">
              {["Stop 1","Stop 2","Stop 3","School A","School B","School C","Home"].map((s,i)=>(
                <div key={i} className="flex items-center gap-1">
                  <div className={`grid h-9 w-9 place-items-center rounded-full ${i<3?"bg-emerald-500/15 text-emerald-600":i<6?"bg-primary/10 text-primary":"bg-muted text-muted-foreground"}`}>
                    {i<3 ? <MapPin className="h-4 w-4" /> : i<6 ? <Building2 className="h-4 w-4" /> : <Home className="h-4 w-4" />}
                  </div>
                  <div>
                    <div className="font-medium">{s}</div>
                    <div className="text-muted-foreground">{i<3?"Pickup":i<6?"Drop":"Return"}</div>
                  </div>
                  {i<6 && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />}
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>


        {/* OPERATORS */}
        <TabsContent value="operators" className="mt-4 space-y-4">
          <Card className="p-4 shadow-card">
            <div className="flex items-center justify-between">
              <div className="font-display text-base">Verified operator network · Uganda</div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => act("Operator leaderboard opened")}><Trophy className="mr-2 h-3.5 w-3.5" />Leaderboard</Button>
                <Button size="sm" onClick={() => act("Operator onboarding flow started · KYC pending")}><Briefcase className="mr-2 h-3.5 w-3.5" />Onboard operator</Button>
              </div>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr><th className="py-2">Operator</th><th>Fleet</th><th>Schools</th><th>Safety</th><th>On-time</th><th>Rating</th><th>Revenue/mo</th><th></th></tr>
                </thead>
                <tbody>
                  {operators.map(o => (
                    <tr key={o.id} className="border-t">
                      <td className="py-2"><div className="flex items-center gap-2"><div className="grid h-7 w-7 place-items-center rounded bg-primary/10 text-primary"><Bus className="h-3.5 w-3.5" /></div><div><div className="font-medium">{o.name}</div>{o.verified && <span className="inline-flex items-center gap-1 text-[10px] text-success"><BadgeCheck className="h-3 w-3" />Verified</span>}</div></div></td>
                      <td>{o.fleet}</td>
                      <td>{o.schools}</td>
                      <td><div className="flex items-center gap-2"><Progress value={o.safety} className="h-1.5 w-16" /><span className="font-mono text-xs">{o.safety}</span></div></td>
                      <td className="font-mono text-xs">{o.onTime}%</td>
                      <td><span className="inline-flex items-center gap-1"><Star className="h-3 w-3 text-amber-500 fill-amber-500" />{o.rating}</span></td>
                      <td className="font-mono text-xs">{fmtCompact(o.revenue)}</td>
                      <td><Button size="sm" variant="ghost"><ChevronRight className="h-4 w-4" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* MARKETPLACE */}
        <TabsContent value="marketplace" className="mt-4 space-y-4">
          {(() => {
            const rfqs = [
              { id: "rfq-1", school:"Greenhill Academy", count:120, district:"Wakiso", budget:"UGX 18–22M/term", deadline: "in 4 days", urgency: "high", routesNeeded: 3, term: "Term 2 · 2026",
                bids: [
                  { op:"Kampala Mobility Co.", price: 19_400_000, fleet: 4, safety: 96, onTime: 97, rating: 4.8, eta: "Term-start ready", verified: true, note: "Pooled with 2 nearby schools — saves 14% vs solo." },
                  { op:"Pearl Schools Transit", price: 20_800_000, fleet: 3, safety: 94, onTime: 95, rating: 4.7, eta: "Term-start ready", verified: true, note: "Premium cabin + GPS tablets included." },
                  { op:"Wakiso Safe Rides", price: 18_200_000, fleet: 4, safety: 92, onTime: 94, rating: 4.6, eta: "+1 week prep", verified: true, note: "Lowest bid · local depot in Nansana." },
                ] },
              { id: "rfq-2", school:"St Mary's College Kisubi", count:84, district:"Wakiso", budget:"UGX 14–18M/term", deadline: "in 6 days", urgency: "medium", routesNeeded: 2, term: "Term 2 · 2026",
                bids: [
                  { op:"Pearl Schools Transit", price: 15_600_000, fleet: 2, safety: 94, onTime: 95, rating: 4.7, eta: "Term-start ready", verified: true, note: "Includes weekend boarding shuttle." },
                  { op:"Entebbe Express Kids", price: 14_900_000, fleet: 3, safety: 95, onTime: 96, rating: 4.7, eta: "Term-start ready", verified: true, note: "Specialist on Entebbe corridor." },
                ] },
              { id: "rfq-3", school:"Mengo Senior School", count:62, district:"Kampala", budget:"UGX 11–14M/term", deadline: "in 9 days", urgency: "low", routesNeeded: 2, term: "Term 2 · 2026",
                bids: [
                  { op:"Kampala Mobility Co.", price: 12_300_000, fleet: 2, safety: 96, onTime: 97, rating: 4.8, verified: true, eta:"Term-start ready", note: "Pooled with Buganda Road PS." },
                  { op:"Wakiso Safe Rides", price: 11_750_000, fleet: 2, safety: 92, onTime: 94, rating: 4.6, verified: true, eta:"Term-start ready", note: "Includes 3 CCTV cameras per bus." },
                ] },
              { id: "rfq-4", school:"Seeta High School", count:96, district:"Mukono", budget:"UGX 16–19M/term", deadline: "in 11 days", urgency: "medium", routesNeeded: 3, term: "Term 2 · 2026",
                bids: [
                  { op:"Mukono Yellow Lines", price: 16_400_000, fleet: 3, safety: 89, onTime: 91, rating: 4.4, verified: true, eta: "Term-start ready", note: "Local fleet, fast deployment." },
                ] },
            ];
            const filtered = rfqs.filter(r =>
              (marketDistrict === "all" || r.district === marketDistrict) &&
              (!marketSearch || r.school.toLowerCase().includes(marketSearch.toLowerCase()))
            );
            const totalBids = rfqs.reduce((a,r)=>a+r.bids.length, 0);
            const totalValue = rfqs.reduce((a,r)=>a+r.bids.reduce((s,b)=>s+b.price,0), 0);
            const avgPrice = totalValue / totalBids;

            return (
              <>
                {/* Marketplace KPIs */}
                <div className="grid gap-3 md:grid-cols-5">
                  <div className="rounded-xl border bg-gradient-to-br from-primary/10 to-card p-3">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground"><FileText className="h-3.5 w-3.5" />Open RFQs</div>
                    <div className="mt-1 font-display text-2xl">{rfqs.length}</div>
                    <div className="text-[10px] text-muted-foreground">{rfqs.reduce((a,r)=>a+r.routesNeeded,0)} routes · {rfqs.reduce((a,r)=>a+r.count,0)} students</div>
                  </div>
                  <div className="rounded-xl border bg-gradient-to-br from-emerald-500/10 to-card p-3">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground"><Hammer className="h-3.5 w-3.5" />Total bids</div>
                    <div className="mt-1 font-display text-2xl">{totalBids}</div>
                    <div className="text-[10px] text-muted-foreground">avg {(totalBids/rfqs.length).toFixed(1)} per RFQ</div>
                  </div>
                  <div className="rounded-xl border bg-gradient-to-br from-amber-500/10 to-card p-3">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground"><DollarSign className="h-3.5 w-3.5" />Avg bid price</div>
                    <div className="mt-1 font-display text-2xl">{fmtCompact(avgPrice)}</div>
                    <div className="text-[10px] text-muted-foreground">per term · per package</div>
                  </div>
                  <div className="rounded-xl border bg-gradient-to-br from-violet-500/10 to-card p-3">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground"><Timer className="h-3.5 w-3.5" />Avg close time</div>
                    <div className="mt-1 font-display text-2xl">4.2d</div>
                    <div className="text-[10px] text-muted-foreground">RFQ → contract signed</div>
                  </div>
                  <div className="rounded-xl border bg-gradient-to-br from-sky-500/10 to-card p-3">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground"><TrendingUp className="h-3.5 w-3.5" />Win-rate (top op)</div>
                    <div className="mt-1 font-display text-2xl">68%</div>
                    <div className="text-[10px] text-muted-foreground">Kampala Mobility Co.</div>
                  </div>
                </div>

                {/* Controls + RFQ grid */}
                <Card className="p-4 shadow-card">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="font-display text-base">Open RFQs · schools ↔ operators</div>
                      <div className="text-xs text-muted-foreground">Schools post transport packages. Verified operators bid. AI scores price, safety & on-time.</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <div className="relative">
                        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                        <Input value={marketSearch} onChange={(e)=>setMarketSearch(e.target.value)} placeholder="Search school…" className="h-9 w-48 pl-7" />
                      </div>
                      <Select value={marketDistrict} onValueChange={setMarketDistrict}>
                        <SelectTrigger className="h-9 w-40"><Filter className="mr-1 h-3.5 w-3.5" /><SelectValue placeholder="District" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All districts</SelectItem>
                          <SelectItem value="Kampala">Kampala</SelectItem>
                          <SelectItem value="Wakiso">Wakiso</SelectItem>
                          <SelectItem value="Mukono">Mukono</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant="outline" onClick={() => act("RFQ export started")}> <Download className="mr-1 h-3.5 w-3.5" />Export</Button>
                      <Button size="sm" onClick={() => setPostRfqOpen(true)}><Plus className="mr-1 h-3.5 w-3.5" />Post RFQ</Button>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-3 lg:grid-cols-2">
                    {filtered.map(r => {
                      const lowest = Math.min(...r.bids.map(b=>b.price));
                      const urgencyMap: Record<string,string> = {high:"bg-rose-500/15 text-rose-600 border-rose-500/30", medium:"bg-amber-500/15 text-amber-600 border-amber-500/30", low:"bg-emerald-500/15 text-emerald-600 border-emerald-500/30"};
                      return (
                        <div key={r.id} className="group relative overflow-hidden rounded-xl border bg-card p-4 transition hover:shadow-md">
                          <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-primary/5 blur-3xl transition group-hover:bg-primary/10" />
                          <div className="relative flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                              <div className="grid h-10 w-10 place-items-center rounded-lg bg-gradient-to-br from-primary to-primary/70 text-primary-foreground"><Building2 className="h-5 w-5" /></div>
                              <div>
                                <div className="font-medium">{r.school}</div>
                                <div className="text-xs text-muted-foreground">{r.district} · {r.count} students · {r.routesNeeded} routes · {r.term}</div>
                                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                                  <Badge variant="outline" className={urgencyMap[r.urgency]}>{r.urgency.toUpperCase()} · closes {r.deadline}</Badge>
                                  <Badge variant="outline">{r.budget}</Badge>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">bids</div>
                              <div className="font-display text-2xl">{r.bids.length}</div>
                            </div>
                          </div>

                          {/* bid distribution bar */}
                          <div className="mt-3">
                            <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground"><span>Bid spread</span><span className="font-mono normal-case">low {fmtCompact(lowest)}</span></div>
                            <div className="mt-1 flex h-1.5 w-full overflow-hidden rounded-full bg-muted">
                              {r.bids.sort((a,b)=>a.price-b.price).map((b,i)=>(
                                <span key={i} title={`${b.op} · ${fmtCompact(b.price)}`} className="h-full" style={{ width: `${100/r.bids.length}%`, background: i===0 ? "hsl(var(--success))" : `hsl(${220-(i*30)} 70% 55%)` }} />
                              ))}
                            </div>
                          </div>

                          {/* top operators preview */}
                          <div className="mt-3 space-y-1">
                            {r.bids.slice(0,2).map((b,i)=>(
                              <div key={i} className="flex items-center justify-between rounded-md bg-muted/40 px-2 py-1 text-[11px]">
                                <span className="flex items-center gap-1.5 truncate"><Star className="h-3 w-3 fill-amber-500 text-amber-500" /><span className="truncate font-medium">{b.op}</span>{b.verified && <BadgeCheck className="h-3 w-3 text-success" />}</span>
                                <span className="font-mono">{fmtCompact(b.price)}</span>
                              </div>
                            ))}
                          </div>

                          <div className="mt-3 flex flex-wrap gap-1.5">
                            <Button size="sm" onClick={() => setBidsRfq(r)}><Eye className="mr-1 h-3 w-3" />View {r.bids.length} bids</Button>
                            <Button size="sm" variant="outline" onClick={() => act(`Message sent to ${r.school}`)}><Send className="mr-1 h-3 w-3" />Ask question</Button>
                            <Button size="sm" variant="outline" onClick={() => act(`Shortlisted ${r.school} RFQ`)}><ThumbsUp className="mr-1 h-3 w-3" />Shortlist</Button>
                          </div>
                        </div>
                      );
                    })}
                    {filtered.length === 0 && <div className="col-span-2 p-8 text-center text-sm text-muted-foreground">No RFQs match the filters.</div>}
                  </div>
                </Card>

                {/* Leaderboard + insights */}
                <div className="grid gap-3 lg:grid-cols-3">
                  <Card className="p-4 shadow-card lg:col-span-2">
                    <div className="flex items-center justify-between">
                      <div className="font-display text-base">Operator leaderboard · win-rate</div>
                      <Badge variant="outline" className="text-[10px]">Last 90 days</Badge>
                    </div>
                    <div className="mt-3 h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={operators.slice(0,7).map(o => ({ name: o.name.split(" ")[0], wins: Math.round(o.onTime * (o.rating/5) * 0.85), bids: Math.round(o.fleet * 1.8) }))}>
                          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar dataKey="bids" fill="hsl(var(--muted-foreground))" radius={[4,4,0,0]} name="Bids placed" />
                          <Bar dataKey="wins" fill="hsl(var(--primary))" radius={[4,4,0,0]} name="Contracts won" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                  <Card className="p-4 shadow-card">
                    <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /><div className="font-display text-base">Marketplace insights</div></div>
                    <ul className="mt-3 space-y-2 text-xs">
                      <li className="rounded-lg border-l-2 border-emerald-500 bg-emerald-50/40 p-2"><div className="font-medium">Greenhill best-fit: Wakiso Safe Rides</div><div className="text-muted-foreground">Lowest price + local depot + 92 safety. Save UGX 1.2M vs avg.</div></li>
                      <li className="rounded-lg border-l-2 border-amber-500 bg-amber-50/40 p-2"><div className="font-medium">Seeta High has only 1 bid</div><div className="text-muted-foreground">Broadcast to Pearl & Kampala Mobility — typical win adds 2.3 bids.</div></li>
                      <li className="rounded-lg border-l-2 border-sky-500 bg-sky-50/40 p-2"><div className="font-medium">Pooling opportunity</div><div className="text-muted-foreground">Mengo + Buganda Road PS share 1.1km — bundle saves ~14%.</div></li>
                    </ul>
                  </Card>
                </div>
              </>
            );
          })()}
        </TabsContent>


        {/* SCHOOLS */}
        <TabsContent value="schools" className="mt-4 space-y-4">
          <Card className="p-4 shadow-card">
            <div className="font-display text-base">Schools using the mobility network</div>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr><th className="py-2">School</th><th>District</th><th>Daily riders</th><th>Routes</th><th>Missed</th><th>Revenue/mo</th></tr>
                </thead>
                <tbody>
                  {(schools as any[]).slice(0,12).map((s,i) => (
                    <tr key={s.id} className="border-t">
                      <td className="py-2 font-medium">{s.name}</td>
                      <td>{s.district ?? "—"}</td>
                      <td>{60 + i*14}</td>
                      <td>{2 + (i%4)}</td>
                      <td><Badge variant={i%5===0?"destructive":"outline"}>{i%5===0?"2 today":"0"}</Badge></td>
                      <td className="font-mono text-xs">{fmtCompact(1_200_000 + i*640_000)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* SAFETY */}
        <TabsContent value="safety" className="mt-4 space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            <Kpi i={ShieldCheck} label="Safe journeys today" value={`${fmtNum(8_119)}`} sub={`${kpis.safeJourneyPct}% verified`} tone="success" />
            <Kpi i={AlertTriangle} label="Missed pickups" value="6" sub="auto-escalated to parents" tone="warn" />
            <Kpi i={Crosshair} label="Wrong-bus boardings" value="3" sub="auto-corrected at next stop" tone="warn" />
            <Kpi i={PhoneCall} label="Emergencies" value="0" sub="last 30 days: 0" tone="success" />
          </div>
          <Card className="p-4 shadow-card">
            <div className="font-display text-base">Safety event log</div>
            <div className="mt-3 space-y-2">
              {safetyEvents.map(ev => (
                <div key={ev.id} className="flex items-center justify-between rounded border p-3 text-sm">
                  <div className="flex items-center gap-3">
                    <div className={`grid h-8 w-8 place-items-center rounded ${ev.severity==="warn"?"bg-amber-500/15 text-amber-600":"bg-sky-500/15 text-sky-600"}`}><AlertTriangle className="h-4 w-4" /></div>
                    <div>
                      <div className="font-medium">{ev.kind}</div>
                      <div className="text-xs text-muted-foreground">{ev.route} · {ev.time} · {ev.student}</div>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => act(`Investigation opened · ${ev.kind}`)}>Investigate</Button>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* DRIVERS */}
        <TabsContent value="drivers" className="mt-4 space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <Card className="p-4 shadow-card">
              <div className="font-display text-base">Driver scorecard · Joseph M.</div>
              <div className="mt-3 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={driverScorecard}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="k" tick={{ fontSize: 11 }} />
                    <PolarRadiusAxis angle={30} domain={[0,100]} tick={{ fontSize: 10 }} />
                    <RRadar dataKey="v" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.4} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card className="p-4 shadow-card">
              <div className="font-display text-base">Top drivers · this week</div>
              <div className="mt-3 space-y-2 text-sm">
                {["Joseph M.","Sarah N.","David K.","Brenda A.","Peter O."].map((n,i)=>(
                  <div key={n} className="flex items-center justify-between rounded border p-2">
                    <div className="flex items-center gap-2"><span className="font-mono text-xs text-muted-foreground w-5">#{i+1}</span><span className="font-medium">{n}</span></div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-muted-foreground">Safety</span>
                      <Progress value={98-i*2} className="h-1.5 w-20" />
                      <span className="font-mono">{98-i*2}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* DEMAND HEATMAP */}
        <TabsContent value="demand" className="mt-4 space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <Card className="p-4 shadow-card">
              <div className="font-display text-base">Demand vs supply · district</div>
              <div className="mt-3 h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={demandByDistrict}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                    <XAxis dataKey="d" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="demand" fill="hsl(var(--primary))" radius={[6,6,0,0]} />
                    <Bar dataKey="supply" fill="hsl(var(--muted-foreground))" radius={[6,6,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card className="p-4 shadow-card">
              <div className="font-display text-base">Fleet utilization · 7 days</div>
              <div className="mt-3 h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={utilization7d}>
                    <defs>
                      <linearGradient id="u1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="util" stroke="hsl(var(--primary))" fill="url(#u1)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
          <Card className="p-4 shadow-card">
            <div className="flex items-center justify-between">
              <div className="font-display text-base">Expansion opportunities</div>
              <Badge variant="outline" className="gap-1"><TrendingUp className="h-3 w-3" />AI-detected</Badge>
            </div>
            <div className="mt-3 grid gap-3 md:grid-cols-3 text-sm">
              {[
                { d:"Wakiso", gap:"+500 seats", n:"3 new routes recommended" },
                { d:"Jinja",  gap:"+300 seats", n:"Underserved Bugembe corridor" },
                { d:"Mbarara",gap:"+200 seats", n:"School-pooling potential 4 schools" },
              ].map((x,i)=>(
                <div key={i} className="rounded-xl border p-3">
                  <div className="font-medium">{x.d}</div>
                  <div className="text-xs text-muted-foreground">{x.n}</div>
                  <Badge className="mt-2" variant="outline">{x.gap}</Badge>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* FINANCE */}
        <TabsContent value="finance" className="mt-4 space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            <Kpi i={Wallet} label="Network revenue · MTD" value={fmtCompact(628_400_000)} sub="+12.4% vs last month" tone="success" />
            <Kpi i={HandCoins} label="Settlements queued" value={fmtCompact(184_200_000)} sub="14 operators · T+1" tone="info" />
            <Kpi i={Gauge} label="Avg cost / student / term" value={fmtUGX(82_500)} sub="-6% YoY (pooling)" tone="primary" />
            <Kpi i={TrendingUp} label="Profit / route" value={fmtCompact(2_780_000)} sub="median across 142 routes" tone="success" />
          </div>
          <Card className="p-4 shadow-card">
            <div className="font-display text-base">Top routes by profit</div>
            <div className="mt-3 space-y-2 text-sm">
              {sharedRoutes.slice(0,5).sort((a,b)=>b.profit-a.profit).map(r => (
                <div key={r.code} className="flex items-center justify-between rounded border p-2">
                  <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{background:r.color}} /><span className="font-mono text-xs">{r.code}</span><span className="text-muted-foreground">{r.name}</span></div>
                  <span className="font-mono text-xs text-success">+{fmtCompact(r.profit)}</span>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* WEATHER */}
        <TabsContent value="weather" className="mt-4 space-y-4">
          <Card className="p-4 shadow-card">
            <div className="flex items-center gap-2 font-display text-base"><CloudRain className="h-4 w-4 text-sky-600" />Weather & road intelligence · Uganda</div>
            <div className="mt-3 grid gap-3 md:grid-cols-3 text-sm">
              {[
                { area:"Kampala–Entebbe", note:"Heavy rain expected 16:00–18:00", action:"Re-route 7 buses via Kajjansi" },
                { area:"Bugembe corridor", note:"Flooding reported at km 4", action:"Suspend Route JIN-01 PM trip" },
                { area:"Mukono–Seeta", note:"Clear conditions", action:"No action" },
              ].map((x,i)=>(
                <div key={i} className="rounded-xl border p-3">
                  <div className="font-medium">{x.area}</div>
                  <div className="text-xs text-muted-foreground">{x.note}</div>
                  <Badge className="mt-2" variant="outline">{x.action}</Badge>
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-4 shadow-card">
            <div className="flex items-center gap-2 font-display text-base"><WifiOff className="h-4 w-4" />Offline Uganda mode</div>
            <p className="mt-2 text-sm text-muted-foreground">Events captured offline by bus tablets — {fmtNum(284)} queued, syncing as network returns. Last sync 4 min ago.</p>
          </Card>
        </TabsContent>

        {/* GREEN */}
        <TabsContent value="green" className="mt-4 space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            <Kpi i={Leaf} label="CO₂ saved · MTD" value="184 tonnes" sub="via school pooling" tone="success" />
            <Kpi i={Gauge} label="Avg km/L fleet" value="6.8" sub="+0.4 vs last month" tone="primary" />
            <Kpi i={Trophy} label="Greenest operator" value="Pearl Schools Transit" sub="score 92" tone="info" />
            <Kpi i={Award} label="ESG schools" value="34" sub="reports auto-generated" tone="warn" />
          </div>
        </TabsContent>

        {/* AI COPILOT */}
        <TabsContent value="copilot" className="mt-4 space-y-4">
          <Card className="p-4 shadow-card">
            <div className="flex items-center gap-2 font-display text-base"><Brain className="h-4 w-4 text-primary" />Mobility AI copilot</div>
            <div className="mt-3 flex gap-2">
              <Input
                value={copilotQ}
                onChange={(e)=>setCopilotQ(e.target.value)}
                onKeyDown={(e)=>{ if(e.key==="Enter" && copilotQ.trim()){ const q=copilotQ.trim(); setCopilotLog(prev=>[{q,a:synthAnswer(q)}, ...prev]); setCopilotQ(""); } }}
                placeholder="Ask: which routes are most profitable? which districts need expansion?"
              />
              <Button onClick={()=>{ if(!copilotQ.trim()) return; const q=copilotQ.trim(); setCopilotLog(prev=>[{q,a:synthAnswer(q)}, ...prev]); setCopilotQ(""); }}>
                <Sparkles className="mr-2 h-3.5 w-3.5" />Ask
              </Button>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {["Most profitable routes","Declining safety operators","Districts needing capacity","Optimize KLA-NORTH-01"].map(s => (
                <button key={s} onClick={()=>{ setCopilotLog(prev=>[{q:s,a:synthAnswer(s)}, ...prev]); }} className="rounded-full border bg-muted/40 px-2.5 py-0.5 text-[11px] hover:bg-muted transition">{s}</button>
              ))}
            </div>
            <div className="mt-4 space-y-3 text-sm">
              {[
                ...copilotLog,
                { q:"Which routes are most profitable?", a:"WAK-02 leads at UGX 4.1M/mo, followed by KLA-NORTH-01 (3.5M). All top 5 are multi-school pooled routes — pooling lifts margins by ~38%." },
                { q:"Which operators have declining safety?", a:"Jinja Education Transit dropped 3 pts (90→87) over 30d. Top contributor: 4 speed events on JIN-01. Auto-flagged for review." },
                { q:"Which districts need more capacity?", a:"Wakiso shows a 500-seat gap, Jinja 300, Mbarara 200. Demand from 9 schools could be served by 4 new pooled routes." },
                { q:"Optimize KLA-NORTH-01?", a:"Re-order stops Naalya → Kiwatule → Ntinda saves 6 min and lifts utilization 52→61. Re-publish?" },
              ].map((c,i)=>(
                <div key={i} className="rounded-xl border bg-card p-3">
                  <div className="text-xs text-muted-foreground">You</div>
                  <div className="font-medium">{c.q}</div>
                  <div className="mt-2 text-xs text-muted-foreground">Copilot</div>
                  <p>{c.a}</p>
                </div>
              ))}
            </div>
          </Card>

        </TabsContent>
      </Tabs>

      {/* DISPATCH OPS DIALOG */}
      <Dialog open={dispatchOpen} onOpenChange={setDispatchOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Siren className="h-5 w-5 text-rose-500" />Dispatch operations center</DialogTitle>
            <DialogDescription>Talk to drivers, broadcast to parents, or escalate to emergency services across the network.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <button onClick={()=>{ act("PTT channel opened with all active drivers"); setDispatchOpen(false); }} className="rounded-xl border bg-card p-4 text-left transition hover:bg-muted/40">
              <PhoneCall className="h-5 w-5 text-primary" />
              <div className="mt-2 font-medium">Open PTT to all drivers</div>
              <div className="text-xs text-muted-foreground">{kpis.tripsActive} active drivers · push-to-talk</div>
            </button>
            <button onClick={()=>{ act("Broadcast sent to 18,420 parents"); setDispatchOpen(false); }} className="rounded-xl border bg-card p-4 text-left transition hover:bg-muted/40">
              <Megaphone className="h-5 w-5 text-sky-600" />
              <div className="mt-2 font-medium">Broadcast to parents</div>
              <div className="text-xs text-muted-foreground">SMS + push · all parents in network</div>
            </button>
            <button onClick={()=>{ setTab("safety"); act("Jumped to safety event log"); setDispatchOpen(false); }} className="rounded-xl border bg-card p-4 text-left transition hover:bg-muted/40">
              <ShieldAlert className="h-5 w-5 text-amber-600" />
              <div className="mt-2 font-medium">Open safety queue</div>
              <div className="text-xs text-muted-foreground">{4} open events to triage</div>
            </button>
            <button onClick={()=>{ toast.error("999 + UPF + school admins notified · ETA 4 min"); setDispatchOpen(false); }} className="rounded-xl border-rose-500/30 bg-rose-500/5 border p-4 text-left transition hover:bg-rose-500/10">
              <Siren className="h-5 w-5 text-rose-600" />
              <div className="mt-2 font-medium text-rose-700">Escalate to 999</div>
              <div className="text-xs text-muted-foreground">Police + Red Cross + school admin</div>
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setDispatchOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BIDS DIALOG */}
      <Dialog open={!!bidsRfq} onOpenChange={(o)=>!o && setBidsRfq(null)}>
        <DialogContent className="max-w-3xl">
          {bidsRfq && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Hammer className="h-5 w-5 text-primary" />Bids · {bidsRfq.school}</DialogTitle>
                <DialogDescription>{bidsRfq.district} · {bidsRfq.count} students · {bidsRfq.routesNeeded} routes · budget {bidsRfq.budget} · closes {bidsRfq.deadline}</DialogDescription>
              </DialogHeader>
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {bidsRfq.bids.sort((a:any,b:any)=>a.price-b.price).map((b:any, i:number) => (
                  <div key={i} className={`rounded-xl border p-4 ${i===0 ? "border-success/40 bg-success/5" : ""}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{b.op}</span>
                          {b.verified && <Badge variant="outline" className="gap-1 border-success/30 text-success text-[10px]"><BadgeCheck className="h-3 w-3" />Verified</Badge>}
                          {i===0 && <Badge className="bg-success/15 text-success text-[10px] border-success/30" variant="outline">Best price</Badge>}
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">{b.fleet} buses · safety {b.safety} · on-time {b.onTime}% · ★ {b.rating} · {b.eta}</div>
                        <div className="mt-2 text-xs">{b.note}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-2xl">{fmtCompact(b.price)}</div>
                        <div className="text-[10px] text-muted-foreground">/ term</div>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      <Button size="sm" onClick={()=>{ act(`Awarded ${b.op} · contract drafted`); setBidsRfq(null); }}><CheckCircle2 className="mr-1 h-3 w-3" />Award contract</Button>
                      <Button size="sm" variant="outline" onClick={()=>act(`Counter-offer sent to ${b.op}`)}><DollarSign className="mr-1 h-3 w-3" />Counter-offer</Button>
                      <Button size="sm" variant="outline" onClick={()=>act(`Message sent to ${b.op}`)}><Send className="mr-1 h-3 w-3" />Message</Button>
                      <Button size="sm" variant="ghost" onClick={()=>act(`Rejected bid from ${b.op}`)}><X className="mr-1 h-3 w-3" />Reject</Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* POST RFQ DIALOG */}
      <Dialog open={postRfqOpen} onOpenChange={setPostRfqOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="h-5 w-5 text-primary" />Post a new RFQ</DialogTitle>
            <DialogDescription>Verified operators in your district will be invited to bid.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1.5"><Label>School</Label><Input placeholder="e.g. Greenhill Academy" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>District</Label>
                <Select defaultValue="Wakiso"><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Kampala">Kampala</SelectItem><SelectItem value="Wakiso">Wakiso</SelectItem><SelectItem value="Mukono">Mukono</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5"><Label>Students</Label><Input type="number" placeholder="120" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Routes needed</Label><Input type="number" placeholder="3" /></div>
              <div className="grid gap-1.5"><Label>Budget (UGX/term)</Label><Input placeholder="18M – 22M" /></div>
            </div>
            <div className="grid gap-1.5"><Label>Notes for operators</Label><Textarea rows={3} placeholder="Safety requirements, route preferences, CCTV needs…" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setPostRfqOpen(false)}>Cancel</Button>
            <Button onClick={()=>{ setPostRfqOpen(false); act("RFQ posted · 14 verified operators notified"); }}><Send className="mr-2 h-3.5 w-3.5" />Post RFQ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shared-route action modal */}
      <Dialog open={!!routeAction} onOpenChange={(o) => !o && setRouteAction(null)}>
        <DialogContent className="max-w-3xl">
          {routeAction && (() => {
            const r = routeAction.route;
            const util = Math.round((r.onboard / r.capacity) * 100);
            const split = r.schools.map((_: any, i: number) => Math.round(100 / r.schools.length) + (i === 0 ? (100 - Math.round(100 / r.schools.length) * r.schools.length) : 0));
            const titles: any = { map: "Live route on map", manifest: "Onboard manifest", fare: "Fare split & settlement", optimize: "AI re-optimization" };
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full" style={{ background: r.color }} />
                    <span className="font-mono text-xs">{r.code}</span>
                    <span>· {titles[routeAction.mode]}</span>
                  </DialogTitle>
                  <DialogDescription>{r.name} · {r.onboard}/{r.capacity} students · {r.stops} stops</DialogDescription>
                </DialogHeader>

                {routeAction.mode === "map" && (
                  <div className="space-y-3">
                    <LiveBusMap height={420} autoTick canViewTrails canViewHistory title={`${r.code} · live`} />
                    <div className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">
                      Showing live telematics for {r.schools.length} schools sharing this corridor. Stops are color-coded by school pickup density.
                    </div>
                  </div>
                )}

                {routeAction.mode === "manifest" && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-4 gap-2 text-center text-xs">
                      <div className="rounded-lg border bg-card p-2"><div className="font-display text-lg">{r.onboard}</div><div className="text-muted-foreground">Onboard</div></div>
                      <div className="rounded-lg border bg-card p-2"><div className="font-display text-lg">{r.capacity - r.onboard}</div><div className="text-muted-foreground">Seats free</div></div>
                      <div className="rounded-lg border bg-card p-2"><div className="font-display text-lg">{util}%</div><div className="text-muted-foreground">Utilization</div></div>
                      <div className="rounded-lg border bg-card p-2"><div className="font-display text-lg">{r.schools.length}</div><div className="text-muted-foreground">Schools</div></div>
                    </div>
                    <div className="max-h-72 overflow-auto rounded-lg border">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/50"><tr><th className="p-2 text-left">Student</th><th className="p-2 text-left">School</th><th className="p-2 text-left">Stop</th><th className="p-2 text-left">Boarded</th></tr></thead>
                        <tbody>
                          {Array.from({ length: r.onboard }).map((_, i) => {
                            const s = r.schools[i % r.schools.length];
                            const names = ["Nakato A.","Mukasa B.","Acan J.","Otim S.","Namugga R.","Lubega K.","Atim P.","Ssali D.","Auma G.","Kato M."];
                            const stops = ["Naalya","Ntinda","Kira","Bukoto","Kamwokya","CBD","Kasubi","Mengo","Wakiso","Entebbe Rd"];
                            return (<tr key={i} className="border-t"><td className="p-2 font-medium">{names[i % names.length]} #{(1000 + i).toString()}</td><td className="p-2 text-muted-foreground">{s}</td><td className="p-2">{stops[i % stops.length]}</td><td className="p-2 font-mono text-emerald-600">06:{(40 + (i % 20)).toString().padStart(2, "0")}</td></tr>);
                          })}
                        </tbody>
                      </table>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => act("Manifest exported to CSV")}><Download className="mr-2 h-3.5 w-3.5" />Export manifest</Button>
                  </div>
                )}

                {routeAction.mode === "fare" && (
                  <div className="space-y-3">
                    <div className="rounded-lg border p-3">
                      <div className="text-xs text-muted-foreground">Monthly revenue pool</div>
                      <div className="font-display text-2xl">{fmtCompact(r.profit)}</div>
                      <div className="mt-2 flex h-3 w-full overflow-hidden rounded-full bg-muted">
                        {r.schools.map((_: any, i: number) => (<span key={i} className="h-full" style={{ width: `${split[i]}%`, background: `hsl(${(i * 97) % 360} 70% 50%)` }} />))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      {r.schools.map((s: string, i: number) => (
                        <div key={i} className="flex items-center justify-between rounded-lg border bg-card p-3 text-sm">
                          <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm" style={{ background: `hsl(${(i * 97) % 360} 70% 50%)` }} /><Building2 className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{s}</span></div>
                          <div className="flex items-center gap-4 text-xs"><span className="font-mono">{split[i]}%</span><span className="font-mono text-success">+{fmtCompact(Math.round(r.profit * split[i] / 100))}</span><Badge variant="outline" className="text-[10px]">auto-settled · Fri</Badge></div>
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Button variant="outline" onClick={() => act("Settlement preview generated")}><FileText className="mr-2 h-3.5 w-3.5" />Generate invoice</Button>
                      <Button onClick={() => { act("Fare split locked for next cycle"); setRouteAction(null); }}><CheckCircle2 className="mr-2 h-3.5 w-3.5" />Lock split</Button>
                    </div>
                  </div>
                )}

                {routeAction.mode === "optimize" && (
                  <div className="space-y-3">
                    <div className="rounded-lg border bg-gradient-to-br from-violet-50 to-sky-50 p-4 dark:from-violet-950/30 dark:to-sky-950/30">
                      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><Brain className="h-3.5 w-3.5" />AI optimizer · simulated</div>
                      <div className="mt-2 font-display text-lg">3 improvements found for {r.code}</div>
                    </div>
                    <div className="space-y-2">
                      {[
                        { t: "Merge Stop 4 (Ntinda) & Stop 5 (Kiwatule)", g: `+${4 + (util % 6)} seats utilization · −2.1km`, save: 180_000 },
                        { t: "Stagger pickup window by 6 min", g: "On-time +3.4% · driver overtime −12%", save: 95_000 },
                        { t: "Swap to 65-seat coach (weekdays)", g: `Util ${util}% → ${Math.min(96, util + 11)}% · CO₂ −18kg/day`, save: 240_000 },
                      ].map((s, i) => (
                        <div key={i} className="flex items-center justify-between rounded-lg border bg-card p-3 text-sm">
                          <div><div className="font-medium">{s.t}</div><div className="text-xs text-muted-foreground">{s.g}</div></div>
                          <div className="flex items-center gap-2"><span className="font-mono text-xs text-success">+{fmtCompact(s.save)}/mo</span><Button size="sm" variant="outline" onClick={() => act(`Applied: ${s.t}`)}>Apply</Button></div>
                        </div>
                      ))}
                    </div>
                    <Button className="w-full" onClick={() => { act(`${r.code} re-optimized · all 3 changes applied`); setRouteAction(null); }}><Zap className="mr-2 h-3.5 w-3.5" />Apply all & re-publish route</Button>
                  </div>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Pool-wide actions: Simulate merge / Re-balance / Optimizer */}
      <Dialog open={!!poolAction} onOpenChange={(o) => !o && setPoolAction(null)}>
        <DialogContent className="max-w-4xl">
          {poolAction === "merge" && (() => {
            const pairs = [
              { a: sharedRoutes[0], b: sharedRoutes[1], overlap: 62, savedKm: 38, savedFuelL: 14, newUtil: 92, mergedProfit: (sharedRoutes[0].profit + sharedRoutes[1].profit) + 480_000, riskMin: 4 },
              { a: sharedRoutes[2], b: sharedRoutes[4], overlap: 47, savedKm: 22, savedFuelL: 9,  newUtil: 88, mergedProfit: (sharedRoutes[2].profit + sharedRoutes[4].profit) + 310_000, riskMin: 6 },
              { a: sharedRoutes[3], b: sharedRoutes[5], overlap: 31, savedKm: 14, savedFuelL: 5,  newUtil: 81, mergedProfit: (sharedRoutes[3].profit + sharedRoutes[5].profit) + 180_000, riskMin: 8 },
            ];
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><Crosshair className="h-4 w-4" />Merge simulator · 3 candidate pairs</DialogTitle>
                  <DialogDescription>Digital twin runs 1,000 trips per pair. Approve to publish merged routes Monday 06:00.</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-4 gap-2 text-center text-xs">
                  <div className="rounded-lg border bg-card p-2"><div className="font-display text-lg text-success">+{fmtCompact(pairs.reduce((s,p)=>s+ (p.mergedProfit - p.a.profit - p.b.profit),0))}</div><div className="text-muted-foreground">extra profit/mo</div></div>
                  <div className="rounded-lg border bg-card p-2"><div className="font-display text-lg">{pairs.reduce((s,p)=>s+p.savedKm,0)} km</div><div className="text-muted-foreground">saved daily</div></div>
                  <div className="rounded-lg border bg-card p-2"><div className="font-display text-lg">{pairs.reduce((s,p)=>s+p.savedFuelL,0)} L</div><div className="text-muted-foreground">fuel/day</div></div>
                  <div className="rounded-lg border bg-card p-2"><div className="font-display text-lg">−3</div><div className="text-muted-foreground">buses freed</div></div>
                </div>
                <div className="space-y-2">
                  {pairs.map((p, i) => {
                    const key = `merge-${i}`;
                    const applied = appliedPool[key];
                    return (
                      <div key={i} className="rounded-lg border bg-card p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="h-2.5 w-2.5 rounded-full" style={{ background: p.a.color }} />
                            <span className="font-mono text-xs">{p.a.code}</span>
                            <span className="text-muted-foreground">+</span>
                            <span className="h-2.5 w-2.5 rounded-full" style={{ background: p.b.color }} />
                            <span className="font-mono text-xs">{p.b.code}</span>
                            <Badge variant="outline" className="ml-2 text-[10px]">{p.overlap}% stop overlap</Badge>
                          </div>
                          <Button size="sm" variant={applied ? "secondary" : "default"} disabled={applied} onClick={() => { setAppliedPool(s => ({ ...s, [key]: true })); act(`Merged ${p.a.code} + ${p.b.code} · scheduled for Mon 06:00`); }}>{applied ? <><CheckCircle2 className="mr-2 h-3.5 w-3.5" />Approved</> : "Approve merge"}</Button>
                        </div>
                        <div className="mt-2 grid grid-cols-4 gap-2 text-[11px]">
                          <div><div className="text-muted-foreground">New utilization</div><div className="font-mono text-emerald-600">{p.newUtil}%</div></div>
                          <div><div className="text-muted-foreground">Saved daily</div><div className="font-mono">{p.savedKm} km · {p.savedFuelL}L</div></div>
                          <div><div className="text-muted-foreground">Profit lift</div><div className="font-mono text-success">+{fmtCompact(p.mergedProfit - p.a.profit - p.b.profit)}</div></div>
                          <div><div className="text-muted-foreground">Avg parent delay</div><div className="font-mono">+{p.riskMin} min</div></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setPoolAction(null)}>Close</Button><Button onClick={() => { pairs.forEach((_,i)=>setAppliedPool(s=>({...s,[`merge-${i}`]:true}))); act("All 3 merges approved · publishing Mon 06:00"); setPoolAction(null); }}><CheckCircle2 className="mr-2 h-3.5 w-3.5" />Approve all</Button></DialogFooter>
              </>
            );
          })()}

          {poolAction === "balance" && (() => {
            const moves = [
              { from: "KLA-NORTH-01", to: "KLA-WEST-04", school: "Kampala Parents School", students: 12, why: "Closer to Bukoto stop · saves 14 min", delta: "+8% util" },
              { from: "WAK-02",       to: "MUK-03",      school: "Mariam High",            students: 9,  why: "Better fare-split balance",        delta: "+6% util" },
              { from: "ENT-01",       to: "JIN-01",      school: "Lake Vic Primary",       students: 6,  why: "Pickup window alignment",          delta: "+4% util" },
              { from: "MUK-03",       to: "KLA-WEST-04", school: "Seeta High",             students: 4,  why: "Underused seats on KLA-WEST-04",   delta: "+3% util" },
            ];
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><Brain className="h-4 w-4" />Re-balance · 38 schools across 47 pooled routes</DialogTitle>
                  <DialogDescription>Shifts students between pooled routes to flatten utilization and shorten commutes. Parents are notified automatically.</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-4 gap-2 text-center text-xs">
                  <div className="rounded-lg border bg-card p-2"><div className="font-display text-lg">{moves.reduce((s,m)=>s+m.students,0)}</div><div className="text-muted-foreground">students moved</div></div>
                  <div className="rounded-lg border bg-card p-2"><div className="font-display text-lg text-emerald-600">78% → 86%</div><div className="text-muted-foreground">avg utilization</div></div>
                  <div className="rounded-lg border bg-card p-2"><div className="font-display text-lg">−11 min</div><div className="text-muted-foreground">avg commute</div></div>
                  <div className="rounded-lg border bg-card p-2"><div className="font-display text-lg text-success">+{fmtCompact(640_000)}</div><div className="text-muted-foreground">monthly margin</div></div>
                </div>
                <div className="max-h-80 space-y-2 overflow-auto">
                  {moves.map((m, i) => {
                    const key = `bal-${i}`;
                    const applied = appliedPool[key];
                    return (
                      <div key={i} className="flex items-center justify-between rounded-lg border bg-card p-3 text-sm">
                        <div className="flex-1">
                          <div className="flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-muted-foreground" /><span className="font-medium">{m.school}</span><Badge variant="outline" className="text-[10px]">{m.students} students</Badge></div>
                          <div className="mt-1 flex items-center gap-2 text-xs"><span className="font-mono">{m.from}</span><ArrowRight className="h-3 w-3" /><span className="font-mono">{m.to}</span><span className="text-muted-foreground">· {m.why}</span></div>
                        </div>
                        <div className="flex items-center gap-2"><span className="font-mono text-xs text-emerald-600">{m.delta}</span><Button size="sm" variant={applied ? "secondary" : "outline"} disabled={applied} onClick={() => { setAppliedPool(s=>({...s,[key]:true})); act(`Moved ${m.students} students · ${m.school} → ${m.to}`); }}>{applied ? "Applied" : "Apply"}</Button></div>
                      </div>
                    );
                  })}
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setPoolAction(null)}>Close</Button><Button onClick={() => { moves.forEach((_,i)=>setAppliedPool(s=>({...s,[`bal-${i}`]:true}))); act("Re-balance applied · 31 parents notified"); setPoolAction(null); }}><Send className="mr-2 h-3.5 w-3.5" />Apply all & notify parents</Button></DialogFooter>
              </>
            );
          })()}

          {poolAction === "optimize" && (() => {
            const sugg = [
              { t: "Stagger pickup windows by 6 min on KLA-NORTH-01", g: "On-time +3.4% · driver overtime −12%", save: 280_000, conf: 0.94 },
              { t: "Swap WAK-02 to 65-seat coach Mon–Wed",            g: "Util 87% → 96% · CO₂ −22kg/day",      save: 410_000, conf: 0.91 },
              { t: "Drop redundant Stop 4 on KLA-WEST-04",            g: "Route −2.1 km · saves 4 min/trip",    save: 180_000, conf: 0.88 },
              { t: "Reverse JIN-01 afternoon order",                  g: "Better traffic window · −7 min",      save: 120_000, conf: 0.82 },
            ];
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><Zap className="h-4 w-4" />AI optimizer · 4 changes suggested across 47 routes</DialogTitle>
                  <DialogDescription>Trained on the last 12 weeks of telematics, fare-split data, parent feedback and weather. Confidence shown per suggestion.</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-4 gap-2 text-center text-xs">
                  <div className="rounded-lg border bg-card p-2"><div className="font-display text-lg text-success">+{fmtCompact(sugg.reduce((s,x)=>s+x.save,0))}</div><div className="text-muted-foreground">monthly impact</div></div>
                  <div className="rounded-lg border bg-card p-2"><div className="font-display text-lg">96.2% → 98.1%</div><div className="text-muted-foreground">on-time</div></div>
                  <div className="rounded-lg border bg-card p-2"><div className="font-display text-lg text-emerald-600">−240 kg</div><div className="text-muted-foreground">CO₂/day</div></div>
                  <div className="rounded-lg border bg-card p-2"><div className="font-display text-lg">0.89</div><div className="text-muted-foreground">avg confidence</div></div>
                </div>
                <div className="space-y-2">
                  {sugg.map((s, i) => {
                    const key = `opt-${i}`;
                    const applied = appliedPool[key];
                    return (
                      <div key={i} className="flex items-center justify-between rounded-lg border bg-card p-3 text-sm">
                        <div className="flex-1">
                          <div className="font-medium">{s.t}</div>
                          <div className="text-xs text-muted-foreground">{s.g}</div>
                          <div className="mt-1 flex items-center gap-2"><Progress value={s.conf * 100} className="h-1 w-32" /><span className="font-mono text-[10px] text-muted-foreground">{Math.round(s.conf * 100)}% confidence</span></div>
                        </div>
                        <div className="flex items-center gap-2"><span className="font-mono text-xs text-success">+{fmtCompact(s.save)}/mo</span><Button size="sm" variant={applied ? "secondary" : "default"} disabled={applied} onClick={() => { setAppliedPool(s2=>({...s2,[key]:true})); act(`Applied: ${s.t}`); }}>{applied ? "Applied" : "Apply"}</Button></div>
                      </div>
                    );
                  })}
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setPoolAction(null)}>Close</Button><Button onClick={() => { sugg.forEach((_,i)=>setAppliedPool(s=>({...s,[`opt-${i}`]:true}))); act("All 4 optimizations applied · routes re-published"); setPoolAction(null); }}><Zap className="mr-2 h-3.5 w-3.5" />Apply all</Button></DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function synthAnswer(q: string): string {
  const x = q.toLowerCase();
  if (x.includes("profit") || x.includes("revenue")) return "Top by margin: WAK-02 (UGX 4.1M/mo · 87% util), KLA-NORTH-01 (3.5M · 80%), KLA-WEST-04 (2.9M · 78%). Pooled routes deliver +38% margin vs solo. 5 underperforming routes are below 50% utilization — flagged for re-route or merge.";
  if (x.includes("safety") || x.includes("decline")) return "Jinja Education Transit dropped 3 pts (90→87) over 30d, driven by 4 speed events on JIN-01. Mukono Yellow Lines also trending down (89→86). Recommend SLA review + driver retraining. Auto-flagged in Operators tab.";
  if (x.includes("capacity") || x.includes("district") || x.includes("expansion")) return "Capacity gaps · Wakiso: +500 seats (3 new pooled routes), Jinja: +300 (Bugembe corridor underserved), Mbarara: +200 (4 schools could pool). Combined revenue opportunity: UGX 38M/month.";
  if (x.includes("optimize") || x.includes("kla-north")) return "Re-order KLA-NORTH-01 stops: Naalya → Kiwatule → Ntinda → Kamwokya. Saves 6 min trip time, lifts utilization 52→61%, and reduces fuel ~9%. Parents notified automatically on Re-publish.";
  if (x.includes("weather") || x.includes("rain")) return "Forecast 16:00–18:00 today: heavy rain over Kampala–Entebbe corridor. 7 buses auto-rerouted via Kajjansi. JIN-01 PM trip suspended due to flooding at Bugembe km 4 — replacement van dispatched.";
  return "Analyzing the network… Top signals: 184 trips active, 96.2% on-time, 0 emergencies, fleet utilization 78% (above seasonal avg). Ask about routes, operators, safety, capacity, weather, or revenue for a deeper view.";
}


/* ---------- helpers ---------- */
function Kpi({ i: I, label, value, sub, tone }: { i: any; label: string; value: string; sub?: string; tone?: "primary"|"success"|"info"|"warn"|"danger" }) {
  const tones: Record<string,string> = {
    primary: "border-primary/30",
    success: "border-emerald-500/30",
    info: "border-sky-500/30",
    warn: "border-amber-500/30",
    danger: "border-destructive/30",
  };
  return (
    <Card className={`p-4 shadow-card ${tone ? tones[tone] : ""}`}>
      <I className="h-4 w-4 text-muted-foreground" />
      <div className="mt-2 font-display text-2xl">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
      {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
    </Card>
  );
}
function Signal({ label, value, tone }: { label: string; value: string; tone: "ok"|"warn"|"danger" }) {
  const dot = { ok: "bg-success", warn: "bg-amber-500", danger: "bg-destructive" }[tone];
  return <div className="flex items-center justify-between"><div className="flex items-center gap-2"><span className={`h-2 w-2 rounded-full ${dot}`} />{label}</div><span className="font-mono text-xs">{value}</span></div>;
}
function Legend2({ color, label }: { color: string; label: string }) {
  return <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />{label}</div>;
}
function Mini({ label, value }: { label: string; value: string }) {
  return <div className="rounded-lg border bg-muted/30 p-2"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div><div className="font-display text-base">{value}</div></div>;
}

function JourneyTimeline() {
  const steps = [
    { time: "06:42", icon: Home, title: "Left home zone", sub: "Naalya Estate · geofence exit", color: "#64748b", verified: true },
    { time: "06:54", icon: MapPin, title: "Arrived pickup stop", sub: "Naalya Gate · Bus 0.4 km away", color: "#0ea5e9", verified: true },
    { time: "07:02", icon: ScanLine, title: "Boarded Bus 14 (KLA-NORTH-01)", sub: "QR scan · seat 12B · Driver Joseph M.", color: "#7B1416", verified: true },
    { time: "07:18", icon: RouteIcon, title: "Route verification passed", sub: "On corridor · no deviation", color: "#16a34a", verified: true },
    { time: "07:38", icon: GraduationCap, title: "School arrival verified", sub: "St Mary's SS Kitende · gate scan", color: "#16a34a", verified: true },
    { time: "08:05", icon: BookOpen, title: "Class attendance confirmed", sub: "S2A · Mathematics · present", color: "#16a34a", verified: true },
    { time: "12:45", icon: CheckCircle2, title: "Lunch verified", sub: "Canteen POS · Meat pie + juice", color: "#16a34a", verified: true },
    { time: "16:18", icon: ScanLine, title: "Boarded return trip", sub: "Bus 14 · QR scan · seat 12B", color: "#7B1416", verified: true },
    { time: "16:52", icon: Home, title: "Arrived home stop", sub: "Naalya Estate · parent notified", color: "#16a34a", verified: true },
  ];
  return (
    <div className="relative">
      <div className="absolute left-[14px] top-2 bottom-2 w-0.5 bg-border" />
      <div className="space-y-4">
        {steps.map((s, i) => {
          const I = s.icon;
          return (
            <div key={i} className="relative flex items-start gap-4">
              <div className="relative z-10 grid h-7 w-7 shrink-0 place-items-center rounded-full ring-4 ring-card" style={{ background: s.color, color: "white" }}>
                <I className="h-3.5 w-3.5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-muted-foreground">{s.time}</span>
                  <span className="font-medium">{s.title}</span>
                  {s.verified && <Badge variant="outline" className="text-[9px]"><CheckCircle2 className="mr-1 h-2.5 w-2.5 text-success" />verified</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">{s.sub}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
